<template>
  <ontos-form-panel v-bind="heading" :saving="saving" :save-disabled="!canSave || edit.unchanged" @save="save()">
    <!-- Os controles são de quem hospeda — o X e o Expandir da janela, o Voltar da página —, com o que
         eles precisam saber: se há o que perder, e o que levar ao expandir -->
    <template #controls>
      <slot name="controls" :dirty="edit.dirty" :resume="resume"/>
    </template>
    <v-form ref="form">
      <!-- A espécie só é escolhida ao criar: mudá-la numa propriedade existente trocaria a natureza do
           campo em todo item já gravado. Em dois passos (pedido do usuário: o espelho é um TIPO de
           relacionamento): Atributo · Relacionamento, e no relacionamento, Novo · Espelho de um
           existente. O segundo só quando há o que espelhar (pedido do usuário): sem candidatos, o
           relacionamento é novo, e o par seria uma escolha sem outra resposta -->
      <div v-if="isCreating" class="property-editor-kind">
        <ontos-pair-toggle :model-value="kind !== 'attribute'" v-bind="kindPairs.species"
                           @update:model-value="kind = $event ? 'relationship' : 'attribute'"/>
        <ontos-pair-toggle v-if="kind !== 'attribute' && mirrorCandidates.length" :model-value="kind === 'reverse'"
                           v-bind="kindPairs.mirror" @update:model-value="kind = $event ? 'reverse' : 'relationship'">
          <ontos-select :items="mirrorCandidates" item-text="label" item-value="key"
                        :label="$tt('property-mirror-of')" hide-details="auto" v-model="mirrorKey"/>
        </ontos-pair-toggle>
      </div>

      <ontos-attribute-input v-if="kind === 'attribute'" ref="input"
                             :value="propertyModel" @input="propertyModel = $event" :idea="idea"
                             :workspace="workspace" :fixed-alias="!isCreating" :resumed="resumedEditor"/>

      <!-- O mesmo editor para o relacionamento e o espelho: os dois lados, na ordem de quem abriu.
           Remonta ao trocar a espécie ou o relacionamento espelhado — ele lê os dois lados ao nascer -->
      <ontos-relationship-input v-else-if="kind === 'relationship' || mirror" ref="input" :key="kind + mirrorKey"
                                :value="propertyModel" @input="propertyModel = $event"
                                :other="otherOriginal" :other-start="resumedOther" @other="otherDraft = $event"
                                :idea="idea" :ideas="workspace.ideas" :workspace="workspace"
                                :fixed-alias="!isCreating" :fixed-destination-class="!isCreating"
                                :resumed="resumedEditor"/>
    </v-form>
  </ontos-form-panel>
</template>
<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'
import Idea from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import {EnhancedWorkpace} from '@/types/Workspace'
import {PropertySave} from '@/types/PropertySave'
import {deepCopy} from '@/utils/copy'
import {designedProperty, DraftState, draftState, updatedProps} from '@/utils/modelUtils'
import AttributeInput from '@/components/design/property/AttributeInput.vue'
import RelationshipInput from '@/components/design/property/RelationshipInput.vue'
import FormPanel from '@/components/shared/FormPanel.vue'
import PairToggle, {PairSide} from '@/components/shared/PairToggle.vue'
import Select from '@/components/shared/Select.vue'
import {Heading, propertyCreate, propertyEdit} from '@/utils/heading'

type Kind = 'attribute' | 'relationship' | 'reverse'
type Mirror = { key: string, label: string, idea: Idea, property: IdeaProperty }

/**
 * O que a janela leva à página ao expandir (pedido do usuário: sem ir ao servidor, com o que se
 * preencheu): a espécie, o espelho escolhido, os dois rascunhos — o outro lado `null` sem espelho — e o
 * que o editor da espécie guarda por dentro (`snapshot`: as opções do domínio mexidas, o nome e o
 * identificador digitados à mão).
 */
export type PropertyResume = {
  kind: Kind, mirrorKey: string, draft: IdeaProperty, otherDraft: IdeaProperty | null, editor: unknown
}

/**
 * Criar e editar propriedade, cobrindo as três espécies — atributo, relacionamento e espelho —, as duas
 * últimas no mesmo editor, com os dois lados da associação. O painel inteiro (`FormPanel`): a janela e a
 * página o hospedam, cada uma com os seus controles — como o `InstanceEdit` faz com a edição de item.
 *
 * Um dono para o que se compara (`edit`): a janela e a página medem o rascunho contra o MESMO original —
 * a propriedade como está no ambiente, ou a semente da espécie ao criar —, e por isso o Salvar e o aviso
 * de saída valem igual nas duas, inclusive depois de expandir (pedido do usuário).
 *
 * Quem persiste é o host: daqui sai um pedido (`PropertySave`) — para onde vai, o corpo, e que coleções
 * reler —, e ele manda e relê.
 */
@Component({emits: ['save'],
  name: 'ontos-property-editor',
  components: {
    'ontos-form-panel': FormPanel,
    'ontos-attribute-input': AttributeInput,
    'ontos-relationship-input': RelationshipInput,
    'ontos-pair-toggle': PairToggle,
    'ontos-select': Select
  }
})
class PropertyEditor extends Vue {
  @Prop({type: Object, required: true}) readonly idea!: Idea
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace
  /** undefined = criando; objeto = editando aquela propriedade */
  @Prop({default: undefined}) readonly property!: IdeaProperty | undefined
  /** o host controla: ele é quem persiste */
  @Prop({type: Boolean, default: false}) readonly saving!: boolean
  /**
   * Criar já no identificador: o "+" da caixa do identificador no design da coleção (pedido do usuário).
   * A propriedade nasce marcada para compô-lo — e obrigatória, que é o que o identificador exige.
   */
  @Prop({type: Boolean, default: false}) readonly identifier!: boolean
  /** O que a janela deixou ao expandir (`PropertyResume`), ou null: começa do gravado, ou da semente. */
  @Prop({type: Object, default: null}) readonly start!: PropertyResume | null

  kind: Kind = 'attribute'
  mirrorKey = ''
  /** cópia de trabalho: editar o objeto do workspace direto mostraria o rascunho na lista */
  draft: IdeaProperty = {name: '', alias: ''}
  original: IdeaProperty = {name: '', alias: ''}
  /** O outro lado da associação como está na tela; null sem espelho. */
  otherDraft: IdeaProperty | null = null
  /**
   * O que veio da janela para o editor da espécie — o que ele guardava por dentro, e o outro lado como
   * estava: ligado, desligado (`null`), ou nada (`undefined`, começa do gravado). Só até o rascunho
   * recomeçar (`resetDraft`): o editor remonta ao trocar de espécie, e ali já não é o da janela.
   */
  resumedEditor: unknown = null
  resumedOther: IdeaProperty | null | undefined = undefined

  created() {
    if (this.property) {
      this.original = designedProperty(this.property)
      this.draft = deepCopy(this.original)
      const relationship = this.property.relationship
      this.kind = !relationship ? 'attribute' : relationship.reverse ? 'reverse' : 'relationship'
      if (relationship && relationship.reverse) {
        this.mirrorKey = `${relationship.idea}.${relationship.reverse}`
      }
      this.otherDraft = this.otherOriginal && deepCopy(this.otherOriginal)
    } else {
      this.resetDraft('attribute')
    }
    //Expandido: o que estava na janela, por cima — o original continua o gravado (ou a semente)
    if (this.start) {
      this.kind = this.start.kind
      this.mirrorKey = this.start.mirrorKey
      this.draft = deepCopy(this.start.draft)
      this.otherDraft = this.start.otherDraft && deepCopy(this.start.otherDraft)
      this.resumedEditor = this.start.editor
      this.resumedOther = this.otherDraft && deepCopy(this.otherDraft)
    }
  }

  get isCreating(): boolean {
    return !this.property
  }

  /** A regra do cabeçalho mora no utils/heading, com a das outras telas */
  get heading(): Heading {
    return this.isCreating ? propertyCreate(this.idea) : propertyEdit(this.idea, this.property!)
  }


  get kindPairs(): Record<'species' | 'mirror', { label: string, off: PairSide, on: PairSide }> {
    return {
      species: {
        label: this.$tt('pair.species') as string,
        off: {label: this.$tt('property-kind-attribute') as string, hint: this.$tt('pair.attribute-hint') as string,
          icon: 'mdi-alphabetical'},
        on: {label: this.$tt('property-kind-relationship') as string, hint: this.$tt('pair.relationship-hint') as string,
          icon: 'mdi-arrow-right', color: 'primary'}
      },
      mirror: {
        label: this.$tt('pair.origin') as string,
        off: {label: this.$tt('pair.new-relationship') as string, hint: this.$tt('pair.new-relationship-hint') as string,
          icon: 'mdi-plus'},
        on: {label: this.$tt('pair.mirror-existing') as string, hint: this.$tt('pair.mirror-existing-hint') as string,
          icon: 'mdi-arrow-left-right', color: 'primary'}
      }
    }
  }

  /**
   * Relacionamentos de outras coleções que apontam para esta e ainda não têm
   * espelho. É a mesma lista que virava um botão por item na barra de ações.
   *
   * O calculado não entra: não tem espelho — o reverso teria de avaliar a fórmula de todos os
   * itens a cada leitura —, e o servidor recusaria.
   */
  get mirrorCandidates(): Mirror[] {
    const candidates: Mirror[] = []
    for (const other of this.workspace.ideas) {
      for (const property of other.properties) {
        const relationship = property.relationship
        if (!relationship || relationship.reverse || relationship.idea !== this.idea.alias || property.formula) continue
        if (this.mirrorOf(other, property)) continue
        candidates.push({
          key: `${other.alias}.${property.alias}`,
          label: `${other.name} · ${property.name}`,
          idea: other,
          property
        })
      }
    }
    return candidates
  }

  /** O espelho gravado do relacionamento `owner`, da coleção `ownerIdea` — mora na coleção de destino. */
  mirrorOf(ownerIdea: Idea, owner: IdeaProperty): IdeaProperty | undefined {
    const target = this.workspace.ideaWith(owner.relationship!.idea)
    return target && target.properties.find(candidate => candidate.relationship
        && candidate.relationship.idea === ownerIdea.alias && candidate.relationship.reverse === owner.alias)
  }

  get mirror(): Mirror | undefined {
    return this.mirrorCandidates.find(candidate => candidate.key === this.mirrorKey)
        || (this.isCreating ? undefined : this.editingMirror)
  }

  /** Ao editar um espelho, o par já está gravado e não é mais candidato */
  get editingMirror(): Mirror | undefined {
    const relationship = this.property && this.property.relationship
    if (!relationship || !relationship.reverse) return undefined
    const other = this.workspace.ideaWith(relationship.idea)
    const property = other && other.properties.find(p => p.alias === relationship.reverse)
    if (!other || !property) return undefined
    return {key: this.mirrorKey, label: `${other.name} · ${property.name}`, idea: other, property}
  }

  /**
   * O outro lado da associação como está gravado: o dono, quando este é o espelho; o espelho do
   * relacionamento editado, se houver. O relacionamento que nasce agora não tem outro lado ainda.
   */
  get otherOriginal(): IdeaProperty | null {
    if (this.kind === 'reverse') return this.mirror ? designedProperty(this.mirror.property) : null
    if (this.kind !== 'relationship' || !this.property) return null
    const mirror = this.mirrorOf(this.idea, this.property)
    return mirror ? designedProperty(mirror) : null
  }

  /**
   * O que muda do outro lado, como o servidor o recebe em `otherSide`: o espelho inteiro, se nasce
   * agora; só o que mudou, se já existia; null para tirá-lo; nada (undefined) se ficou como estava.
   */
  get otherSide(): Record<string, unknown> | null | undefined {
    if (!this.otherDraft) return this.otherOriginal ? null : undefined
    if (!this.otherOriginal) return this.otherDraft
    const changes = updatedProps(this.otherOriginal, this.otherDraft)
    return Object.keys(changes).length ? changes : undefined
  }

  get canSave(): boolean {
    return this.kind !== 'reverse' || !!this.mirror
  }

  /**
   * O que mudou, se o salvar habilita e se sair pergunta — a regra dos diálogos de edição
   * (`draftState`), com o outro lado da associação somado: mudar só o espelho também é mudança. Ao
   * criar, a semente é a da espécie escolhida — trocar de espécie recomeça o rascunho e não conta como
   * digitação. Pelo `updatedProps`: o servidor troca as chaves de cima inteiras.
   */
  get edit(): DraftState {
    const own = draftState(this.isCreating ? undefined : this.original, this.draft, this.seedDraft(this.kind), updatedProps)
    const otherChanged = this.otherSide !== undefined
    return {changes: own.changes, unchanged: own.unchanged && !otherChanged, dirty: own.dirty || otherChanged}
  }

  get propertyModel(): IdeaProperty {
    return this.draft
  }

  set propertyModel(property: IdeaProperty) {
    this.draft = property
    //itemCount nasce depois do objeto: sem $set o min/max não seria reativo
    if (property.itemCount) {
      this.draft.itemCount = property.itemCount
      if (!isNaN(property.itemCount.min)) (this.draft.itemCount as any).min = property.itemCount.min
      if (!isNaN(property.itemCount.max)) (this.draft.itemCount as any).max = property.itemCount.max
    }
  }

  resetDraft(kind: Kind) {
    this.draft = this.seedDraft(kind)
    this.otherDraft = this.otherOriginal && deepCopy(this.otherOriginal)
    this.resumedEditor = null
    this.resumedOther = undefined
  }

  /**
   * A propriedade recém-aberta, antes de qualquer digitação: opcional, como o relacionamento (pedido do
   * usuário — o atributo nascia obrigatório). Pelo "+" do identificador, já marcada para compô-lo, e
   * por isso obrigatória; o espelho não, que não oferece a marca.
   */
  seedDraft(kind: Kind): IdeaProperty {
    const inIdentifier = this.identifier ? {identifier: true, required: true} : {}
    if (kind === 'attribute') return {name: '', alias: '', type: 'text', ...inIdentifier}
    if (kind === 'relationship') return {name: '', alias: '', relationship: {idea: ''}, ...inIdentifier}
    const mirror = this.mirror
    return {
      name: '', alias: '',
      relationship: mirror
          ? {idea: mirror.idea.alias, reverse: mirror.property.alias}
          : {idea: ''}
    }
  }

  /**
   * O rascunho é desta espécie — e, no espelho, do espelho escolhido? Trocar de espécie recomeça o
   * rascunho só quando ele não é dela: os campos de uma não valem para a outra. Pelo rascunho, e não
   * pela troca: a página que recebe a janela expandida põe a espécie e o rascunho juntos, e recomeçar ali
   * apagaria o que veio.
   */
  draftFits(): boolean {
    const relationship = this.draft.relationship
    if (this.kind === 'attribute') return !relationship
    if (this.kind === 'relationship') return !!relationship && !relationship.reverse
    return !!relationship && `${relationship.idea}.${relationship.reverse}` === this.mirrorKey
  }

  /**
   * Só ao CRIAR. Ao editar, o created() deriva `kind` da propriedade, e essa primeira atribuição também
   * conta como mudança — o watcher então apagava a cópia que o created() tinha acabado de fazer, e o
   * formulário abria em branco. O seletor de espécie, de resto, só existe enquanto se cria.
   */
  @Watch('kind')
  onKindChange() {
    if (this.isCreating && !this.draftFits()) this.resetDraft(this.kind)
  }

  //Mesma razão: editar um espelho define o mirrorKey no created()
  @Watch('mirrorKey')
  onMirrorChange() {
    if (this.isCreating && this.kind === 'reverse' && !this.draftFits()) this.resetDraft('reverse')
  }

  /**
   * Para onde vai e o quê (`PropertySave`).
   *
   * O espelho novo de um relacionamento que já existe se grava pelo DONO: `PATCH` nele, com o que mudou
   * de lá e o espelho em `otherSide` — é o mesmo caminho do espelho criado pelo dono, e o servidor grava
   * os dois numa transação. Nos demais casos, na coleção aberta, com o outro lado junto se ele mudou.
   * Relê as duas pontas quando o outro lado vai junto.
   */
  saveRequest(): PropertySave {
    const common = {name: this.draft.name, creating: this.isCreating}
    if (this.kind === 'reverse' && this.isCreating) {
      const owner = this.mirror!
      return {...common, ideaAlias: owner.idea.alias, propertyAlias: owner.property.alias,
        body: {...(this.otherSide || {}), otherSide: this.draft}, reload: [owner.idea.alias, this.idea.alias]}
    }
    const otherSide = this.otherSide
    const body: Record<string, unknown> = this.isCreating ? {...this.draft} : {...this.edit.changes}
    if (otherSide !== undefined) body.otherSide = otherSide
    const otherIdea = otherSide !== undefined && this.draft.relationship ? this.draft.relationship.idea : this.idea.alias
    return {...common, ideaAlias: this.idea.alias, propertyAlias: this.isCreating ? undefined : this.original.alias,
      body, reload: [...new Set([this.idea.alias, otherIdea])]}
  }

  /** Valida e entrega o pedido; quem grava é o host. */
  async save() {
    const input = this.$refs.input as any
    if (!input || !input.validateModel()) return
    //O que vai junto e precisa ir antes — hoje, as opções do domínio de um atributo de seleção
    if (!await input.commit()) return
    this.$emit('save', this.saveRequest())
  }

  /** O que levar ao expandir (`PropertyResume`) — cópias: a janela fecha logo depois. */
  resume(): PropertyResume {
    const input = this.$refs.input as { snapshot?(): unknown } | undefined
    return {kind: this.kind, mirrorKey: this.mirrorKey, draft: deepCopy(this.draft),
      otherDraft: this.otherDraft && deepCopy(this.otherDraft),
      editor: input && input.snapshot ? input.snapshot() : null}
  }
}

export default toNative(PropertyEditor)
</script>

<style scoped>
.property-editor-kind {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
  margin-bottom: 24px;
}
</style>
